import {
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { catchError, of } from 'rxjs';
import { DialogArchivosReservasComponent } from 'src/app/file/services/dialog-archivos-reservas/dialog-archivos-reservas.component';
import { FunctionsService } from 'src/app/services/api/functions.service';
import {
  applyEditAmountResultToTicketContext,
  canEditTicketAmount,
  isSsffCatalogBlockedOnChild
} from 'src/entities/file';
import {
  applyConfirmedPreliminaryOrder,
  applyDeleteOrderDialogResult,
  applyFreezeDeadlineToServices,
  applyOrderUpdateToServices,
  formatScheduledPaymentDate,
  OrderService
} from 'src/entities/order';
import { Ticket } from 'src/entities/ticket';
import { DialogEditFechaPagoProgramadaComponent } from 'src/features/service-order/edit-scheduled-payment-date/ui/dialog-edit-fecha-pago-programada/dialog-edit-fecha-pago-programada.component';
import { buildEditFechaPagoDialogConfig } from 'src/features/service-order/edit-scheduled-payment-date/ui/dialog-edit-fecha-pago-programada/edit-fecha-pago-dialog.config';
import {
  OrderPdfDialogComponent,
  OrderPdfDialogResult
} from 'src/features/service-order/order-pdf/ui/order-pdf-dialog/order-pdf-dialog.component';
import { buildOrderPdfDialogConfig } from 'src/features/service-order/order-pdf/ui/order-pdf-dialog/order-pdf-dialog.config';
import { NotaCreditoProveedorComponent } from 'src/features/service-order/services-os-actions/ui/nota-credito-proveedor/nota-credito-proveedor.component';
import { DialogEditTicketAmountComponent } from 'src/features/ticketeria/edit-ticket-amount/ui/dialog-edit-ticket-amount/dialog-edit-ticket-amount.component';
import { buildEditTicketAmountDialogConfig } from 'src/features/ticketeria/edit-ticket-amount/ui/dialog-edit-ticket-amount/edit-ticket-amount-dialog.config';
import { SERVICE_TYPES } from '../../services/service-types';
import { ServicioFormatterService } from '../ticketeriaHeleper';
import { EditarNumberTicketsComponent } from './editar-number-tickets/editar-number-tickets.component';

/** Labels UI para estadoTicket (Core). */
const ESTADO_TICKET_LABEL: Record<string, string> = {
  CREADO: 'Creado',
  CREATED: 'Creado',
  EN_PROCESO: 'En Proceso',
  RESERVADO: 'Reservado',
  CONFIRMADO: 'Confirmado'
};

@Component({
  selector: 'app-table-expand',
  templateUrl: './table-expand.component.html',
  styleUrls: ['./table-expand.component.css']
})
export class TableExpandComponent implements OnInit {
  dataSource = new MatTableDataSource<any>([]);
  readonly estadoTicketLabel = ESTADO_TICKET_LABEL;

  constructor(
    public dialog: MatDialog,
    private readonly functions: FunctionsService,
    private readonly servicioFormatter: ServicioFormatterService,
    private readonly orderService: OrderService
  ) {}

  @Input() allData: any[] = [];
  @Input() data: any[] = [];
  @Input() file: any = {};
  /** Estilos embebidos (detalle file / OS masivas). No oculta checks por sí solo. */
  @Input() viewOnly = false;
  /**
   * Checkbox por ticket libre para Generar OS.
   * Default: true en Ticketería (!viewOnly); en embedded hay que pasar true explícito.
   */
  @Input() allowTicketSelect: boolean | null = null;

  /** Sticker SSFF / file padre: se pasa desde detalle/OS masivas; Ticketería usa file.isSSFFPadre. */
  @Input() isParentFile = false;

  @HostBinding('class.table-expand--embedded')
  get isEmbedded(): boolean {
    return this.viewOnly;
  }

  get canSelectTickets(): boolean {
    return this.allowTicketSelect ?? !this.viewOnly;
  }

  /** File padre (programación SSFF) o fila Ticketería de padre. */
  private resolveIsParentFile(): boolean {
    return this.isParentFile || Boolean(this.file?.isSSFFPadre);
  }

  /** Catálogo SSFF solo en file hijo: sin editar / sin checkbox OS. */
  private isSsffCatalogBlocked(): boolean {
    return isSsffCatalogBlockedOnChild(this.file, this.resolveIsParentFile());
  }

  @Output() selectionChange = new EventEmitter<any>();
  @Output() changeDato = new EventEmitter<{
    id: string;
    idTicket: string;
    changesTickets: any;
    hasInvalidFechaOperacion?: any;
    changeGeneral: any;
  }>();

  @Output() onChangeDatosMasivos = new EventEmitter<{
    ticketsCambiados: any[];
  }>();

  /** Padre debe invalidar/recargar tickets de estos servicios (crear/borrar OS). */
  @Output() ticketsOrderChanged = new EventEmitter<{
    serviceId: string;
    serviceIds?: string[];
  }>();

  ngOnInit(): void {
    this.refreshTicketRows();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['allData'] || changes['data'] || changes['file']) {
      this.refreshTicketRows();
    }
  }

  private readonly allDisplayedColumns: string[] = [
    'select',
    'fechaCreacion',
    'tipoTicket',
    'monto',
    'estadoTicket',
    'orderStatus',
    'idOS',
    'fechaOSGenerada',
    'orderNumber',
    'verOS',
    'scheduledPaymentDate',
    'paidAt',
    'orderObservation',
    'archivos'
  ];

  get displayedColumns(): string[] {
    return this.canSelectTickets
      ? this.allDisplayedColumns
      : this.allDisplayedColumns.filter((column) => column !== 'select');
  }

  @ViewChild(MatSort) set sort(value: MatSort) {
    this.dataSource.sort = value;
  }

  updateCheck(row: any, event: any) {
    const checked = Boolean(event?.checked);
    // Mantener en sync la fila del dataSource y la fuente (this.data).
    row.checked = checked;
    const source = (this.data ?? []).find((t: any) => t.id === row.id);
    if (source) {
      source.checked = checked;
    }
    this.selectionChange.emit({
      servicioId: this.file.id,
      ticketId: row.id,
      selected: checked
    });
  }

  normalizeMoneda(moneda: string | null | undefined): string {
    if (!moneda) return 'S/';
    const value = moneda.toUpperCase();
    if (
      value === 'D' ||
      value === 'DOLAR' ||
      value === 'USD' ||
      value === '$'
    ) {
      return '$';
    }
    return 'S/';
  }

  /** TC del servicio padre primero; ticket solo como fallback. */
  private resolveTipoCambioFromParent(ticket?: any): number | null {
    const candidates = [
      this.file?.tipoCambio,
      this.file?.objTarifa?.tipoCambio,
      ticket?.tipoCambio,
      ticket?.objTarifa?.tipoCambio
    ];
    for (const raw of candidates) {
      const value = Number(raw);
      if (Number.isFinite(value) && value > 0) {
        return value;
      }
    }
    return null;
  }

  /** Moneda del servicio padre primero. */
  private resolveTipoMonedaFromParent(ticket?: any): string | null {
    const candidates = [
      this.file?.tipoMoneda,
      this.file?.moneda,
      ticket?.tipoMoneda,
      ticket?.objTarifa?.monedaTarifa,
      ticket?.moneda
    ];
    for (const raw of candidates) {
      if (raw != null && String(raw).trim() !== '') {
        return String(raw);
      }
    }
    return null;
  }

  normalizeEstadoTicket(estado: string | null | undefined): string {
    if (!estado) return '';
    const key = estado.toUpperCase().replace(/\s+/g, '_');
    if (key === 'CREATED') return 'CREADO';
    if (key === 'ENPROCESO') return 'EN_PROCESO';
    return key;
  }

  formatTicket(ticket: Ticket) {
    const order = ticket.order ?? null;
    const estadoTicket = this.normalizeEstadoTicket(ticket.estadoTicket);
    const paxTickets = ticket.paxTickets ?? null;
    const objTarifa = ticket.objTarifa ?? null;

    return {
      // Core ticket (paint-ready)
      id: ticket.id,
      checked: ticket.checked ?? false,
      createdAt: ticket.createdAt,
      paxTickets,
      objTarifa,
      tipoCambio: this.resolveTipoCambioFromParent(ticket),
      ticketTipos: paxTickets
        ? this.servicioFormatter.handleTipoTicket(paxTickets)
        : [],
      total: ticket.total ?? null,
      monto: ticket.total ?? null,
      moneda: this.normalizeMoneda(
        ticket.moneda ??
          objTarifa?.monedaTarifa ??
          this.file?.tipoMoneda ??
          this.file?.moneda ??
          null
      ),
      tipoMoneda:
        ticket.moneda ??
        objTarifa?.monedaTarifa ??
        this.file?.tipoMoneda ??
        this.file?.moneda ??
        null,
      estadoTicket,
      hasDocument: ticket.hasDocument,
      hasInvalidFechaOperacion: Boolean(ticket.hasInvalidFechaOperacion),
      // OS solo desde order{} (null ⇒ sin OS / columnas “—”)
      order,
      hasOrder: order != null,
      startDateService: this.file?.startDateService
    };
  }

  private refreshTicketRows(): void {
    this.dataSource.data = (this.data ?? []).map((item: Ticket) =>
      this.formatTicket(item)
    );
  }

  handleBlockEditMonto(element: any) {
    return (
      this.handleBlockByEstadoTicket(element, 'editarMonto') ||
      this.isSsffCatalogBlocked() ||
      !canEditTicketAmount(element, this.file, this.resolveIsParentFile())
    );
  }

  /**
   * Editar cantidades: requiere objTarifa.tarifaTicket (Core),
   * ticket en CREADO, sin OS.
   * Con OS no preliminar: no se edita este ticket (al aumentar nace otro; no se disminuye).
   * PRELIMINAR sí se edita en el mismo ticket.
   * File hijo + catálogo SSFF: bloqueado. Padre / serviceAdd: permitido.
   * paxTickets en 0 NO bloquea (hay que poder asignar pax).
   */
  private orderLocksTicketQuantity(element: any): boolean {
    const hasOrder = Boolean(
      element?.order?.orderId || element?.order?.orderNumber
    );
    if (!hasOrder) {
      return false;
    }
    const status = String(element?.order?.orderStatus || '')
      .trim()
      .toUpperCase();
    return status !== 'PRELIMINAR';
  }

  handleBlockEditNumberTickets(element: any): boolean {
    if (this.isSsffCatalogBlocked()) {
      return true;
    }
    if (this.orderLocksTicketQuantity(element)) {
      return true;
    }
    if (!element?.objTarifa?.tarifaTicket) {
      return true;
    }
    const status = String(element?.order?.orderStatus || '')
      .trim()
      .toUpperCase();
    if (status === 'PRELIMINAR') {
      return false;
    }
    return this.handleBlockByEstadoTicket(element, 'editarTicketPax');
  }

  handleBlockByEstadoTicket(element: any, columna: string) {
    const estado = this.normalizeEstadoTicket(element.estadoTicket);
    const estadosAllowed: Record<string, string[]> = {
      archivos: ['RESERVADO', 'CONFIRMADO'],
      acciones: ['EN_PROCESO'],
      editarMonto: ['CREADO'],
      editarTicketPax: ['CREADO']
    };

    if (estadosAllowed[columna]?.includes(estado)) return false;
    return true;
  }

  handleBlockByEstadoOS(element: any, columna: string) {
    if (!element.order?.orderId) {
      return true;
    }

    const status = element.order?.orderStatus?.toUpperCase();
    const estadosAllowed: Record<string, string[]> = {
      eliminar: ['CONFIRMADO'],
      editarFechaPago: ['CONFIRMADO']
    };

    return !estadosAllowed[columna]?.includes(status);
  }

  openDialogArchivo(ticket: any) {
    if (this.handleBlockByEstadoTicket(ticket, 'archivos')) return;

    const dialogConfig = new MatDialogConfig();
    const rawDate = ticket.startDateService ?? this.file?.startDateService;
    let formattedDate = '-';
    if (rawDate) {
      const parts = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(rawDate).trim());
      if (parts) {
        formattedDate = `${parts[3]}/${parts[2]}/${parts[1]}`;
      } else {
        const d = new Date(rawDate);
        if (!Number.isNaN(d.getTime())) {
          const day = d.getUTCDate().toString().padStart(2, '0');
          const month = (d.getUTCMonth() + 1).toString().padStart(2, '0');
          formattedDate = `${day}/${month}/${d.getUTCFullYear()}`;
        }
      }
    }

    dialogConfig.data = {
      id: ticket.id,
      fileId: this.file.idItinerary,
      serviceId: this.file.id,
      serviceType: SERVICE_TYPES.TICKET,
      serviceName: 'Documentos: Tickets',
      hasDocument: ticket.hasDocument,
      hasInvalidFechaOperacion: ticket.hasInvalidFechaOperacion,
      fileNumber: this.file.file?.fileNumber,
      fecha: formattedDate,
      isTicketItineraryService: true
    };
    dialogConfig.width = '1000px';
    dialogConfig.minHeight = '379px';
    dialogConfig.maxHeight = '900px';

    this.dialog
      .open(DialogArchivosReservasComponent, dialogConfig)
      .afterClosed()
      .subscribe((result) => {
        if (!result) return;

        ticket.hasDocument = result.changeIconColor;
        const changesTickets: any = {
          hasDocument: result.changeIconColor
        };

        if (result.changeIconColor === true) {
          ticket.estadoTicket = 'CONFIRMADO';
          changesTickets.estadoTicket = 'CONFIRMADO';
        }

        if (result.hasInvalidFechaOperacion != null) {
          ticket.hasInvalidFechaOperacion = result.hasInvalidFechaOperacion;
          changesTickets.hasInvalidFechaOperacion =
            result.hasInvalidFechaOperacion;
        }

        this.changeDato.emit({
          id: this.file.id,
          idTicket: ticket.id,
          changesTickets,
          hasInvalidFechaOperacion: result.hasInvalidFechaOperacion,
          changeGeneral: null
        });
        this.refreshTicketRows();
      });
  }

  isPreliminary(element: any): boolean {
    return element?.order?.orderStatus === 'PRELIMINAR';
  }

  /** Fila “servicio” para reutilizar las acciones OS de un servicio normal. */
  ticketServiceRow(element: any): any {
    return {
      ...this.file,
      order: element?.order
    };
  }

  onTicketPreliminaryChanged(element: any): void {
    // La OS puede abarcar tickets de varios padres: propagar la
    // confirmación y la fecha «Confirmar antes del».
    const changed = element?.order;
    if (
      changed?.orderId &&
      changed.orderStatus &&
      changed.orderStatus !== 'PRELIMINAR'
    ) {
      applyConfirmedPreliminaryOrder(this.data ?? [], changed);
      applyConfirmedPreliminaryOrder(this.allData ?? [], changed);
    }
    if (changed?.orderId && changed.freezeDeadlineAt) {
      applyFreezeDeadlineToServices(
        this.data ?? [],
        changed,
        changed.freezeDeadlineAt
      );
      applyFreezeDeadlineToServices(
        this.allData ?? [],
        changed,
        changed.freezeDeadlineAt
      );
    }
    this.emitTicketsOrderChangedForServices(
      this.collectServiceIdsForOrder(
        element?.order?.orderNumber,
        element?.order?.orderId
      )
    );
    this.refreshTicketRows();
  }

  openDialogNotaCreditoProveedorComponent(ticket: any): void {
    this.dialog.open(NotaCreditoProveedorComponent, {
      width: '800px',
      minHeight: '490px',
      data: {
        fileNumber: this.file.file?.fileNumber,
        nameProveedor: this.file.providerSnapshot?.nombre,
        idProveedor: this.file.providerSnapshot?.id,
        numberOS: ticket.order?.orderNumber,
        idItinerary: this.file.idItinerary,
        moneda: this.file.moneda !== 'S/' ? 'Dólares' : 'Soles'
      }
    });
  }

  openDialogEliminarOS(element: any) {
    if (
      this.handleBlockByEstadoTicket(element, 'acciones') ||
      this.handleBlockByEstadoOS(element, 'eliminar') ||
      !element.order?.orderId
    ) {
      return;
    }

    const serviceWithOrder = {
      ...this.file,
      order: element.order
    };
    const orderNumber = element.order?.orderNumber;
    const orderId = element.order?.orderId;
    const affectedServiceIds = [
      ...this.collectServiceIdsForOrder(orderNumber, orderId),
      ...((element.order?.serviceIds as string[]) || []).map(String)
    ];

    this.functions
      .openDeleteOrderDialog(serviceWithOrder, this.allData, 'Ticketería')
      .afterClosed()
      .subscribe((result) => {
        if (!result?.success && !result?.shouldRefresh) {
          return;
        }

        applyDeleteOrderDialogResult(
          this.allData,
          result?.success
            ? result
            : {
                success: true,
                orderNumber: result?.orderNumber ?? orderNumber
              },
          serviceWithOrder
        );
        this.emitTicketsOrderChangedForServices(affectedServiceIds);
        this.refreshTicketRows();
        if (!result?.success && result?.shouldRefresh) {
          this.functions.disparadorCambios.emit('reload-after-create-order');
        }
      });
  }

  /** Padres con la OS en sí o en tickets hijos (antes de limpiar local). */
  private collectServiceIdsForOrder(
    orderNumber: string | number | null | undefined,
    orderId?: string | null
  ): string[] {
    const ids = new Set<string>();
    const currentId = String(
      this.file?.id || this.file?.serviceId || ''
    ).trim();
    if (currentId) ids.add(currentId);

    for (const service of this.allData ?? []) {
      const serviceKey = String(service?.id || service?.serviceId || '').trim();
      if (!serviceKey) continue;

      const parentMatch =
        (orderNumber != null &&
          orderNumber !== '' &&
          String(service?.order?.orderNumber) === String(orderNumber)) ||
        (orderId && String(service?.order?.orderId) === String(orderId));

      const ticketMatch =
        Array.isArray(service?.tickets) &&
        service.tickets.some(
          (ticket: any) =>
            (orderNumber != null &&
              orderNumber !== '' &&
              String(ticket?.order?.orderNumber) === String(orderNumber)) ||
            (orderId && String(ticket?.order?.orderId) === String(orderId))
        );

      if (parentMatch || ticketMatch) {
        ids.add(serviceKey);
      }
    }

    return [...ids];
  }

  private emitTicketsOrderChangedForServices(serviceIds: string[]): void {
    const unique = [...new Set(serviceIds.filter(Boolean))];
    if (!unique.length) return;
    this.ticketsOrderChanged.emit({
      serviceId: unique[0],
      serviceIds: unique
    });
  }

  openDialogEditarMonto(element: any) {
    if (this.handleBlockEditMonto(element)) return;

    const tipoCambio = this.resolveTipoCambioFromParent(element);
    const tipoMoneda = this.resolveTipoMonedaFromParent(element);

    const dialogRef = this.dialog.open(
      DialogEditTicketAmountComponent,
      buildEditTicketAmountDialogConfig({
        ticketId: String(element.id),
        serviceId: String(this.file.id),
        currentTotal:
          element.monto != null
            ? Number(element.monto)
            : element.total != null
              ? Number(element.total)
              : null,
        tickets: this.dataSource.data,
        tipoCambio,
        moneda: tipoMoneda ?? element.moneda,
        tipoMoneda,
        objTarifa: element.objTarifa,
        servicioTipoCambio: tipoCambio,
        nombreServicio: this.file?.nombreServicio
      })
    );

    dialogRef.afterClosed().subscribe((closeResult) => {
      if (closeResult?.success !== true || !closeResult.result) return;

      const result = closeResult.result;
      applyEditAmountResultToTicketContext({
        tickets: this.data,
        ticketRow: element,
        serviceRow: this.file,
        result
      });

      const ticketMonto =
        result.ticket?.total != null
          ? Number(result.ticket.total)
          : Number(element.total ?? element.monto);
      const serviceTotal =
        result.serviceTotal?.amount != null
          ? Number(result.serviceTotal.amount)
          : result.services?.[0]?.total != null
            ? Number(result.services[0].total)
            : ticketMonto;

      this.changeDato.emit({
        id: this.file.id,
        idTicket: element.id,
        changesTickets: {
          total: ticketMonto,
          monto: ticketMonto,
          moneda: result.ticket?.moneda ?? element.moneda,
          editOperation: true,
          newTotalCostoSoles: result.ticket?.newTotalCostoSoles,
          newTotalCostoDolares: result.ticket?.newTotalCostoDolares
        },
        changeGeneral: {
          total: serviceTotal,
          editOperation: true,
          newTotalCostoSoles: result.services?.[0]?.newTotalCostoSoles,
          newTotalCostoDolares: result.services?.[0]?.newTotalCostoDolares
        }
      });
      this.refreshTicketRows();
    });
  }

  openDialogEditarNumberTickets(element: any) {
    if (this.handleBlockEditNumberTickets(element)) {
      return;
    }

    const tarifaTicket = element.objTarifa?.tarifaTicket;
    const tipoCambio = this.resolveTipoCambioFromParent(element);
    const tipoMoneda = this.resolveTipoMonedaFromParent(element);

    const dialogRef = this.dialog.open(EditarNumberTicketsComponent, {
      autoFocus: false,
      width: '1000px',
      data: {
        id: element.id,
        paxTickets: element.paxTickets,
        tarifaTicket,
        moneda: this.normalizeMoneda(tipoMoneda ?? element.moneda),
        tipoCambio,
        tipoMoneda,
        servicioId: this.file.id,
        tickets: this.dataSource.data
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.success !== true) {
        return;
      }

      const paxTickets =
        result.ticketsChanges?.paxTickets ?? element.paxTickets;
      const total =
        element.moneda === '$'
          ? result.ticketsChanges?.newTotalCostoDolares
          : result.ticketsChanges?.newTotalCostoSoles;

      const sourceTicket = (this.data ?? []).find(
        (t: any) => t.id === element.id
      );
      if (sourceTicket) {
        Object.assign(sourceTicket, {
          paxTickets,
          total: total ?? sourceTicket.total,
          ...(result.ticketsChanges ?? {})
        });
      }

      this.changeDato.emit({
        id: this.file.id,
        idTicket: element.id,
        changesTickets: {
          ...(result.ticketsChanges ?? {}),
          paxTickets,
          total: total ?? element.total
        },
        changeGeneral: result.servicesChanges
      });
      this.refreshTicketRows();

      // La cantidad se guarda vía sales-api: hay que recalcular la OS.
      const orderId = element?.order?.orderId;
      const orderStatus = String(element?.order?.orderStatus ?? '')
        .trim()
        .toUpperCase();
      if (orderId && orderStatus === 'PRELIMINAR') {
        this.orderService
          .recalculatePreliminaryOrder(orderId)
          .pipe(catchError(() => of(null)))
          .subscribe();
      }
    });
  }

  openDialogEditarFechaPago(element: any) {
    if (
      this.handleBlockByEstadoOS(element, 'editarFechaPago') ||
      !element.order?.orderId
    ) {
      return;
    }

    const serviceWithOrder = {
      ...this.file,
      order: element.order
    };

    const dialogRef = this.dialog.open(
      DialogEditFechaPagoProgramadaComponent,
      buildEditFechaPagoDialogConfig({ service: serviceWithOrder })
    );

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.shouldRefresh) {
        this.functions.disparadorCambios.emit('reload-after-create-order');
        return;
      }

      if (!result?.success) {
        return;
      }

      const scheduledPaymentDate =
        result.order?.scheduledPaymentDate ??
        (result.newDate
          ? formatScheduledPaymentDate(
              result.newDate instanceof Date
                ? result.newDate
                : new Date(result.newDate)
            )
          : null);

      const orderPatch = {
        ...(result.order ?? {}),
        scheduledPaymentDate
      };

      // Mutar tickets fuente (this.data), no solo la fila formateada del MatTable.
      // refreshTicketRows() re-mapea desde this.data; si no se actualiza aquí, se pierde el cambio.
      applyOrderUpdateToServices(
        this.data ?? [],
        result.orderNumber ?? element.order?.orderNumber,
        orderPatch,
        (this.data ?? []).find((t: any) => t.id === element.id)
      );

      // También actualizar filas padre si comparten orderNumber (allData = servicios)
      applyOrderUpdateToServices(
        this.allData ?? [],
        result.orderNumber ?? element.order?.orderNumber,
        orderPatch,
        serviceWithOrder
      );

      this.refreshTicketRows();
    });
  }

  showChecboxOption(element: any) {
    if (!this.canSelectTickets) {
      return false;
    }
    // Padre: todo OK. Hijo catálogo SSFF: no. serviceAdd / upgrade: sí.
    if (this.isSsffCatalogBlocked()) {
      return false;
    }
    if (!(Number(this.file?.total) > 0)) {
      return false;
    }
    if (element?.order?.orderId) {
      return false;
    }

    return this.normalizeEstadoTicket(element?.estadoTicket) === 'CREADO';
  }

  openDialogVerOS(ele: any): void {
    const orderId = ele.order?.orderId;
    if (!orderId) return;

    this.dialog
      .open(
        OrderPdfDialogComponent,
        buildOrderPdfDialogConfig({
          orderId,
          orderNumber: ele.order?.orderNumber,
          pdfStatus: ele.order?.pdfStatus ?? null,
          lastPdfError: ele.order?.lastPdfError ?? null
        })
      )
      .afterClosed()
      .subscribe((result: OrderPdfDialogResult | undefined) => {
        if (!result?.pdfStatus || !ele.order) return;

        ele.order = {
          ...ele.order,
          pdfStatus: result.pdfStatus,
          pdfGeneratedAt: result.pdfGeneratedAt ?? ele.order.pdfGeneratedAt,
          lastPdfError: result.lastPdfError ?? null
        };

        applyOrderUpdateToServices(
          this.allData,
          ele.order?.orderNumber,
          {
            pdfStatus: result.pdfStatus,
            pdfGeneratedAt: result.pdfGeneratedAt ?? null,
            lastPdfError: result.lastPdfError ?? null
          },
          { ...this.file, order: ele.order }
        );
      });
  }

  isRowInvalid(row: any): boolean {
    const key = `ticket_status_${row.id}`;
    return localStorage.getItem(key) === 'false';
  }
}
